/** Expiry-only Query leases; never the agreement-wide billing purge/authority. */
import { useLayoutEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { ApiError, isApiError, companySoftwareBillingApi as api, canonicalModuleOfferLocator,
  sameModuleOfferLocator as sameId, type CompanySoftwareBillingScope, type CompanyPurchaseAcceptedCopy,
  type CompanyPurchasePage, type CompanySoftwareExpiryApi } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import { sameCompanyBillingScope } from "@bw/shared/utils";
import type { CompanyExpirySelection, CompanyExpiryReview, CompanyExpiryCommand, CompanyExpiryResult,
  CompanyExpiryAttempt, CompanyExpiryDenial, CompanySoftwareExpiryHooks } from "./companyExpiryContracts";
import { exactKeys, uuid, companyBillingQueryKey } from "./useModuleBilling";

type Operation = keyof CompanySoftwareExpiryApi;
type ModuleKey = CompanyPurchaseAcceptedCopy["offer"]["module_key"];
export const companyExpiryQueryKey = (scope: CompanySoftwareBillingScope) => ["company-software-expiry", scope.actorId,
  canonicalModuleOfferLocator(scope.orgId), scope.environment, canonicalModuleOfferLocator(scope.acceptanceId)] as const;
export function sameExpirySelection(left: CompanyExpirySelection, right: CompanyExpirySelection): boolean {
  return sameId(left.purchase_id, right.purchase_id) && sameId(left.operation_id, right.operation_id)
    && sameId(left.acceptance_id, right.acceptance_id) && left.created_at === right.created_at;
}
const selectionKey = (original: CompanyExpirySelection | null) => original === null ? null
  : [canonicalModuleOfferLocator(original.purchase_id), canonicalModuleOfferLocator(original.operation_id),
    canonicalModuleOfferLocator(original.acceptance_id), original.created_at];
const reviewKey = (scope: CompanySoftwareBillingScope, moduleKey: ModuleKey, revision: number, original: CompanyExpirySelection | null) =>
  [...companyExpiryQueryKey(scope), moduleKey, revision, "getExpiryReview", selectionKey(original)] as const;

export class CompanyExpiryRequestError extends ApiError {
  constructor(readonly scope: CompanySoftwareBillingScope, readonly expiryFenceRevision: number,
    readonly purchaseId: string, readonly operation: Operation, status: number) {
    super(status, "COMPANY_EXPIRY_UNAVAILABLE", "Module-end information is unavailable. Review access before retrying.");
  }
}
export function companyExpiryDenial(error: unknown): CompanyExpiryDenial | null {
  return error instanceof CompanyExpiryRequestError && [401, 403].includes(error.status)
    ? { scope: error.scope, expiryFenceRevision: error.expiryFenceRevision, purchaseId: error.purchaseId,
      operation: error.operation, category: error.status === 401 ? "identity_or_scope_lost" : "expiry_authority_unproven" } : null;
}
function selection(original: CompanyExpirySelection, scope: CompanySoftwareBillingScope): void {
  exactKeys(original, ["purchase_id", "operation_id", "acceptance_id", "created_at"]);
  if (![original.purchase_id, original.operation_id, original.acceptance_id].every(uuid)
    || !sameId(original.acceptance_id, scope.acceptanceId) || typeof original.created_at !== "string"
    || !Number.isFinite(Date.parse(original.created_at))) throw new Error("Original module-end selection changed");
}
function agreement(client: QueryClient, scope: CompanySoftwareBillingScope, moduleKey: ModuleKey, original: CompanyExpirySelection,
  value: CompanyExpiryReview | CompanyExpiryResult): void {
  selection(original, scope);
  const context = value.scope.execution_context;
  if (value.scope.module_key !== moduleKey
    || value.scope.action !== "module_expiry" || context.purpose !== "module_expiry"
    || !sameId(context.acceptance_id, original.acceptance_id) || !sameId(context.purchase_id, original.purchase_id)
    || context.environment !== scope.environment || !sameId(value.scope.org_id, scope.orgId)) throw new Error("Module-end agreement mismatch");
  // Existing canonical Query facts, not another original registry. Off-page
  // selection remains usable, but any known contradictory row must be rejected.
  for (const [, page] of client.getQueriesData<CompanyPurchasePage>({ queryKey: [...companyBillingQueryKey(scope), "listPurchases"] })) {
    if (!page) continue;
    if (!sameId(page.org_id, scope.orgId) || !sameId(page.acceptance_id, scope.acceptanceId) || page.environment !== scope.environment)
      throw new Error("Original history scope changed");
    for (const row of page.items) if (sameId(row.purchase_id, original.purchase_id) && !sameExpirySelection(row, original))
      throw new Error("Original module-end record changed");
  }
  if ("lane" in value && value.lane.current_purchase !== null && sameId(value.lane.current_purchase.purchase_id, original.purchase_id)
    && !sameExpirySelection(value.lane.current_purchase, original)) throw new Error("Module-end result original changed");
}
export function expiryCommand(review: CompanyExpiryReview): CompanyExpiryCommand {
  const context = review.scope.execution_context;
  if (review.state !== "eligible" || !review.can_confirm || context.purpose !== "module_expiry"
    || review.command_id === null || review.review_fingerprint === null) throw new Error("A current module-end review is required");
  return Object.freeze({ environment: context.environment, purchase_id: context.purchase_id, command_id: review.command_id,
    reviewed_fingerprint: review.review_fingerprint, expected_lane_revision: context.expected_lane_revision,
    expected_licence_revision: context.expected_licence_revision });
}
export function sameExpiryCommand(left: CompanyExpiryCommand, right: CompanyExpiryCommand): boolean {
  return left.environment === right.environment && sameId(left.purchase_id, right.purchase_id) && sameId(left.command_id, right.command_id)
    && left.reviewed_fingerprint === right.reviewed_fingerprint && left.expected_lane_revision === right.expected_lane_revision
    && left.expected_licence_revision === right.expected_licence_revision;
}
function sameReviewScope(left: CompanyExpiryReview, right: CompanyExpiryResult): boolean {
  const a = left.scope.execution_context, b = right.scope.execution_context;
  return a.purpose === "module_expiry" && b.purpose === "module_expiry" && sameId(left.scope.org_id, right.scope.org_id)
    && left.scope.module_key === right.scope.module_key && a.environment === b.environment && sameId(a.purchase_id, b.purchase_id)
    && sameId(a.acceptance_id, b.acceptance_id) && a.terms_fingerprint === b.terms_fingerprint
    && a.expected_lane_revision === b.expected_lane_revision && a.expected_licence_revision === b.expected_licence_revision
    && JSON.stringify(left.scope.facility_ids.map(canonicalModuleOfferLocator).sort())
      === JSON.stringify(right.scope.facility_ids.map(canonicalModuleOfferLocator).sort());
}

/** Every invocation captures a monotonic lease. Recovered access cannot revive
 * a token invalidated by loss, selection changes, suspension or actor teardown.
 * Cleanup owns ONLY the purpose prefix, and only on actual scope teardown.
 */
function useExpiryLease(scope: CompanySoftwareBillingScope, moduleKey: ModuleKey, revision: number, original: CompanyExpirySelection | null, suspended: boolean) {
  const { user, loading } = useAuth(), client = useQueryClient();
  const scopeId = JSON.stringify(companyExpiryQueryKey(scope)), id = JSON.stringify([scopeId, moduleKey, revision, selectionKey(original), user?.uid, loading, suspended]);
  const live = useRef({ id, scope, active: true, token: Symbol() });
  if (live.current.id !== id) live.current = { id, scope, active: live.current.active, token: Symbol() };
  useLayoutEffect(() => { live.current.active = true; return () => {
    live.current.active = false; live.current.token = Symbol();
    void client.cancelQueries({ queryKey: companyExpiryQueryKey(scope) }); client.removeQueries({ queryKey: companyExpiryQueryKey(scope) });
  }; }, [scopeId, client]);
  const fail = () => new CompanyExpiryRequestError(scope, revision, original?.purchase_id ?? "", "getExpiryReview", 503);
  return {
    capture: () => {
      const token = live.current.token;
      const ensure = () => {
        if (!live.current.active || live.current.id !== id || live.current.token !== token || loading || user?.uid !== scope.actorId
          || !scope.actorId || original === null || typeof suspended !== "boolean" || suspended
          || !["academy", "insights", "competitions"].includes(moduleKey) || !Number.isSafeInteger(revision) || revision < 0
          || !sameCompanyBillingScope(live.current.scope, scope)) throw fail();
        selection(original, scope);
      };
      ensure(); return ensure;
    },
    invalidate: () => { live.current.token = Symbol(); },
  };
}

export function useCompanyExpiryReview(scope: CompanySoftwareBillingScope, expectedModuleKey: ModuleKey, expiryFenceRevision: number,
  suspendAutomaticReads: boolean, original: CompanyExpirySelection | null): ReturnType<CompanySoftwareExpiryHooks["useCompanyExpiryReview"]> {
  const client = useQueryClient(), lease = useExpiryLease(scope, expectedModuleKey, expiryFenceRevision, original, false);
  return useQuery({ queryKey: reviewKey(scope, expectedModuleKey, expiryFenceRevision, original), enabled: !suspendAutomaticReads && original !== null, retry: false,
    queryFn: async () => {
      const ensure = lease.capture();
      if (original === null) throw new Error("No module-end original selected");
      try {
        const value = await api.getExpiryReview(scope.orgId, original.purchase_id, scope.environment);
        ensure(); agreement(client, scope, expectedModuleKey, original, value); return value;
      } catch (error) {
        ensure();
        if (isApiError(error) && [401, 403].includes(error.status)) lease.invalidate();
        throw new CompanyExpiryRequestError(scope, expiryFenceRevision, original.purchase_id, "getExpiryReview", isApiError(error) ? error.status : 503);
      }
    },
  });
}

export function useCompleteCompanyExpiry(scope: CompanySoftwareBillingScope, expectedModuleKey: ModuleKey, expiryFenceRevision: number,
  suspendDispatch: boolean, original: CompanyExpirySelection | null): ReturnType<CompanySoftwareExpiryHooks["useCompleteCompanyExpiry"]> {
  const client = useQueryClient(), lease = useExpiryLease(scope, expectedModuleKey, expiryFenceRevision, original, suspendDispatch);
  return useMutation<CompanyExpiryResult, Error, CompanyExpiryAttempt>({ retry: false,
    mutationFn: async attempt => {
      const ensure = lease.capture();
      if (original === null || attempt.expiryFenceRevision !== expiryFenceRevision || !sameCompanyBillingScope(attempt.scope, scope)
        || !sameExpirySelection(attempt.original, original)) throw new Error("Module-end command scope changed");
      const prior = client.getQueryData<CompanyExpiryReview>(reviewKey(scope, expectedModuleKey, expiryFenceRevision, original));
      if (!prior || !sameExpiryCommand(expiryCommand(prior), attempt.request)) throw new Error("Module-end review changed");
      agreement(client, scope, expectedModuleKey, original, prior);
      try {
        const value = await api.completeExpiry(scope.orgId, original.purchase_id, attempt.request);
        ensure(); agreement(client, scope, expectedModuleKey, original, value);
        if (!sameReviewScope(prior, value)) throw new Error("Module-end result review changed");
        return value;
      } catch (error) {
        ensure();
        if (isApiError(error) && [401, 403].includes(error.status)) lease.invalidate();
        throw new CompanyExpiryRequestError(scope, expiryFenceRevision, original.purchase_id, "completeExpiry", isApiError(error) ? error.status : 503);
      }
    },
  });
}
